import React, { useMemo, useState } from 'react';
import { CheckCheck, CheckCircle2, ClipboardList, Clock, CornerUpLeft, Eye, ListChecks, Search, ShieldCheck, XCircle } from 'lucide-react';
import * as hr from '../../api';
import { useHR } from '../../HRContext';
import { Select } from '../../components/Controls';
import { download, toCSV } from '../../format';
import { Chip, Kpi } from '../attendance/shared';
import { Empty, STATUS_LABEL, StatusChip, TypeChip, WAITING, dmy, num, payrollImpact, useFrozen } from './shared';

// Advanced Dashboard > Approval (Stephanet's leavemgmt/Approval.jsx): pending requests as cards (Review opens Pre-Approve / Approve /
// Reject / Return with a comment), then every request with the balance before and after it and its payroll impact

const short = s => (s ? new Date(`${s}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—');
const shortY = s => (s ? new Date(`${s}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const STAGE = { pending: 'Awaiting supervisor', pre_approved: 'Awaiting HR final approval' };
const IMPACT_TONE = { 'Paid - No Deduction': 'green', 'Unpaid - Salary Deduction': 'red', Pending: 'yellow' };
const DONE = { approve: 'approved', pre_approve: 'pre-approved', reject: 'rejected', return: 'returned for correction' };

export default function Approval({ data, reload, onView, tick }) {
  const { notify } = useHR();
  const [open, setOpen] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const reqs = data.requests;
  const pending = reqs.filter(r => WAITING.includes(r.status)).sort((a, b) => a.start_date.localeCompare(b.start_date));
  const rows = useMemo(() => reqs.filter(r => (!status || (status === 'waiting' ? WAITING.includes(r.status) : r.status === status))
    && (!q || [r.name, r.reference, r.department, r.leave_type].some(v => String(v || '').toLowerCase().includes(q.toLowerCase())))), [reqs, q, status]);
  const ref = useFrozen(2, [tick, rows.length]);
  const can = data.can.approve;

  const act = (r, action) => {
    if (['reject', 'return'].includes(action) && !comment.trim()) { notify('Enter the reason in the comment box first', 'bad'); return; }
    setBusy(action);
    hr.leaveAction({ ids: [r.id], action, comment })
      .then(res => {
        if (res.failed.length) notify(res.failed[0], 'bad');
        else { notify(`${r.reference} (${r.name}): ${DONE[action]}`); setOpen(null); setComment(''); }
        return reload();
      })
      .catch(e => notify(e.message, 'bad')).finally(() => setBusy(''));
  };
  const toggle = r => { setOpen(o => (o === r.id ? null : r.id)); setComment(''); };
  const exportCsv = () => download(`leave-approval-register-${data.today}.csv`, toCSV([
    { label: 'Request ID', get: r => r.reference }, { label: 'Employee', get: r => r.name }, { label: 'Department', get: r => r.department },
    { label: 'Type', get: r => r.leave_type }, { label: 'Start', get: r => r.start_date }, { label: 'End', get: r => r.end_date },
    { label: 'Working Days', get: r => r.days }, { label: 'Balance Before', get: r => r.balance_before ?? '' }, { label: 'Balance After', get: r => r.balance_after ?? '' },
    { label: 'Status', get: r => STATUS_LABEL[r.status] }, { label: 'Approver', get: r => r.approved_by || r.pre_approved_by || '' }, { label: 'Payroll Impact', get: r => payrollImpact(r) },
  ], rows));

  return (
    <>
      <div className="md-kpis mds-kpis lvm-kpis3" key={`a${tick}`}>
        <Kpi label="Pending Approval" value={pending.length} Ico={Clock} tone="amber" note={`${pending.filter(r => r.status === 'pre_approved').length} awaiting HR final`} />
        <Kpi label="Approved" value={reqs.filter(r => r.status === 'approved').length} Ico={CheckCircle2} tone="green" note="Taken from balances" i={1} />
        <Kpi label="Total Requests" value={reqs.length} Ico={ListChecks} tone="blue" note="All leave requests" i={2} />
      </div>

      <div className="mdr-card lvm-appr">
        <div className="lvm-appr-title"><Clock size={15} /> PENDING APPROVAL ({pending.length})</div>
        {!pending.length && <div className="mdr-empty">Nothing is waiting for approval.</div>}
        <div className="lvm-appr-list">
          {pending.map((r, i) => {
            const isOpen = open === r.id;
            const lacking = r.balance_before !== null && r.days > r.balance_before;
            return (
              <div key={r.id} className={`lvm-appr-card s-${r.status} ${isOpen ? 'open' : ''}`} style={{ animationDelay: `${Math.min(i, 10) * 40}ms` }}>
                <div className="lvm-appr-main">
                  <div className="lvm-appr-who">
                    <b>{r.name}</b>
                    <small>{[r.employee_no, r.department, r.grade].filter(Boolean).join(' · ')}</small>
                  </div>
                  <div className="lvm-appr-facts">
                    <TypeChip t={r.leave_type} />
                    <span>{short(r.start_date)} → {shortY(r.end_date)}</span>
                    <b>{r.days} days</b>
                    <span className="lvm-pay">Payroll: {payrollImpact(r)}</span>
                    <Chip tone={r.status === 'pre_approved' ? 'blue' : 'yellow'}>{STAGE[r.status]}</Chip>
                  </div>
                  <p className="lvm-appr-reason"><span>Reason:</span> {r.reason || '—'}</p>
                  <p className={`lvm-appr-bal ${lacking ? 'bad' : ''}`}><span>Balance Before:</span> <b>{r.balance_before === null ? 'Not tracked' : `${num(r.balance_before)} days`}</b>
                    {r.balance_before !== null && <> <span className="lvm-arrow">→ after approval:</span> <b>{num(r.balance_before - r.days)} days</b></>}
                    {lacking && <em> — not enough balance</em>}</p>
                  {r.pre_approved_by && <p className="lvm-appr-reason"><span>Pre-approved by:</span> {r.pre_approved_by}</p>}
                </div>
                <div className="lvm-appr-side">
                  <button className={`mds-run sm ${isOpen ? 'lvm-active' : ''}`} onClick={() => toggle(r)}><ClipboardList size={13} /> Review</button>
                  <button className="mds-view" onClick={() => onView(r.id)}><Eye size={12} /> Details</button>
                </div>
                {isOpen && (
                  <div className="lvm-appr-review">
                    {can ? <>
                      <textarea rows={2} value={comment} onChange={e => setComment(e.target.value)} placeholder="Comment for the employee (required to reject or return)…" />
                      <div className="lvm-appr-btns">
                        {r.status === 'pending' && <button className="mds-run sm" disabled={!!busy} onClick={() => act(r, 'pre_approve')}><ShieldCheck size={13} /> {busy === 'pre_approve' ? 'Saving…' : 'Pre-Approve'}</button>}
                        <button className="mds-run sm lvm-ok" disabled={!!busy || lacking} title={lacking ? 'Not enough balance' : ''} onClick={() => act(r, 'approve')}><CheckCheck size={13} /> {busy === 'approve' ? 'Saving…' : 'Approve'}</button>
                        <button className="mds-run sm att2-danger" disabled={!!busy} onClick={() => act(r, 'reject')}><XCircle size={13} /> {busy === 'reject' ? 'Saving…' : 'Reject'}</button>
                        <button className="mds-run sm lvm-warn" disabled={!!busy} onClick={() => act(r, 'return')}><CornerUpLeft size={13} /> {busy === 'return' ? 'Saving…' : 'Return'}</button>
                      </div>
                    </> : <p className="lvm-hint">You need approve rights on Leave Management to act on requests.</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mdr-card mdr-table-card">
        <div className="mds-table-head">
          <b>ALL LEAVE REQUESTS</b>
          <div className="att2-filters no-print">
            <div className="mdr-search mds-quick"><Search size={14} /><input placeholder="Search..." value={q} onChange={e => setQ(e.target.value)} /></div>
            <Select value={status} onChange={e => setStatus(e.target.value)}><option value="">All Status</option><option value="waiting">Pending</option>
              {['approved', 'rejected', 'returned', 'cancelled', 'draft'].map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}</Select>
            {data.can.export && <button className="mdr-btn" onClick={exportCsv} disabled={!rows.length}>Export</button>}
          </div>
        </div>
        <div className="mdr-scroll">
          <table className="mdr-table mds-table lvm-frozen lvm-f2" ref={ref}>
            <thead><tr><th>Request ID</th><th>Employee</th><th>Type</th><th>Period</th><th className="c">Working Days</th><th className="c">Balance Before</th>
              <th className="c">Balance After</th><th>Status</th><th>Approver</th><th>Payroll Impact</th></tr></thead>
            <tbody key={tick}>
              {rows.map((r, i) => (
                <tr key={r.id} className="mdr-row lvm-click" style={{ animationDelay: `${Math.min(i, 20) * 15}ms` }} onClick={() => onView(r.id)}>
                  <td className="mdr-id">{r.reference}</td>
                  <td className="lvm-emp"><b>{r.name}</b><small>{r.department || '—'}</small></td>
                  <td><TypeChip t={r.leave_type} /></td>
                  <td className="nowrap">{dmy(r.start_date)} → {dmy(r.end_date)}</td>
                  <td className="c b">{r.days}</td>
                  <td className="c">{num(r.balance_before)}</td>
                  <td className={`c b ${r.balance_after === null || r.balance_after === undefined ? '' : r.balance_after < 0 ? 'rd' : 'g'}`}>{num(r.balance_after)}</td>
                  <td><StatusChip s={r.status} /></td>
                  <td className="nowrap">{r.approved_by || (r.pre_approved_by ? `${r.pre_approved_by} (pre)` : '—')}</td>
                  <td><Chip tone={IMPACT_TONE[payrollImpact(r)] || 'gray'}>{payrollImpact(r)}</Chip></td>
                </tr>
              ))}
              {!rows.length && <Empty cols={10}>No leave requests match.</Empty>}
            </tbody>
          </table>
        </div>
        <div className="mdr-foot att2-foot"><span>Balance before / after: the year's credit (entitlement + carried + adjusted − monetized) less approved leave in date order · pending requests show the balance they would draw on</span></div>
      </div>
    </>
  );
}
